import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { camelizeKeys } from './wire-format';
import { environment } from 'src/environments/environment';

export interface AiConversationSummary {
  id: string;
  title: string | null;
  createdDate: string;
  updatedDate: string;
  messageCount: number;
}

export interface AiSourceReference {
  type: string;
  reference: string;
  label?: string | null;
  /** Application-relative route to the record, when one exists. */
  url?: string | null;
  /** "image", "video", "audio", "pdf"… when this reference is a file; absent for plain records. */
  mediaType?: string | null;
  /** Direct URL of the file itself, as opposed to `url`, which routes to its parent record. */
  mediaUrl?: string | null;
  caption?: string | null;
}

export interface AiChartSeries {
  label: string;
  values: number[];
  /** "primary" or "secondary" — a second scale is used only for genuinely different units. */
  axis?: 'primary' | 'secondary';
}

/**
 * A chart the assistant produced. Labels and values are resolved server-side from authorized
 * queries — the model selects a dataset, it never supplies figures.
 */
/**
 * Presentation intents, not Chart.js type names. Several map to the same renderer configured
 * differently — a horizontal bar is a bar with a swapped index axis, an area chart is a filled
 * line — and the mapping lives in the chart component.
 */
export type AiChartType =
  | 'bar'
  | 'horizontal_bar'
  | 'stacked_bar'
  | 'grouped_bar'
  | 'line'
  | 'area'
  | 'pie'
  | 'doughnut'
  | 'radar'
  | 'polar_area';

export interface AiChartSpec {
  id: string;
  chartType: AiChartType;
  title: string;
  description?: string | null;
  xAxisLabel?: string | null;
  yAxisLabel?: string | null;
  labels: string[];
  series: AiChartSeries[];
  recordCount: number;

  /** The underlying data was capped, so the figures themselves may be incomplete. */
  truncated: boolean;

  /**
   * Points the chart was given but did not draw — usually because it is showing the top few.
   *
   * Distinct from `truncated`: what is drawn here is accurate, there is simply more of it. The two
   * were one flag once, so a chart deliberately showing the top ten of thirteen announced itself as
   * "a sample", which teaches readers that the warning means nothing.
   */
  omittedPoints: number;
}

export interface AiTableColumn {
  /** Field name in the source result, kept so a column can be traced back to its data. */
  field: string;
  header: string;
  /** True when every value present parsed as a number; drives alignment only. */
  isNumeric: boolean;
}

/**
 * A table the assistant produced, offered to the user as an Excel or PDF download.
 *
 * Cells are resolved server-side from an authorized tool result — the model chooses which result
 * and which columns, never what a cell says — and arrive already formatted, so the screen, the
 * spreadsheet and the PDF cannot round a figure three different ways.
 */
export interface AiTableSpec {
  id: string;
  title: string;
  description?: string | null;
  columns: AiTableColumn[];
  /** One entry per row, aligned to `columns`. A null cell was absent in the source. */
  rows: (string | null)[][];
  recordCount: number;

  /** The underlying data was capped, so these may not be all the matching records. */
  truncated: boolean;

  /** Rows the source held that this table does not carry. Presentation, not correctness. */
  omittedRows: number;

  /**
   * Whose records these are, in the words the tool used.
   *
   * Travels into the exported file: the same table means something different to a project head
   * and to an agent, and whoever receives a forwarded spreadsheet cannot reconstruct which.
   */
  scope?: string | null;
}

export interface AiMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string | null;
  sources: AiSourceReference[];
  charts?: AiChartSpec[];
  tables?: AiTableSpec[];
  createdDate: string;
  errorMessage?: string | null;
}

export interface AiConversationDetail {
  id: string;
  title: string | null;
  createdDate: string;
  messages: AiMessage[];
}

export interface AiExecutionStep {
  tool: string;
  outcome: string;
  records: number;
}

export interface AiSendMessageResponse {
  conversationId: string;
  title: string | null;
  assistantMessage: AiMessage;
  confirmationRequired: boolean;
  pendingToolName?: string | null;
  executionSummary: AiExecutionStep[];
  hitIterationLimit: boolean;
  /** Suggested next questions for this turn. */
  followUps: string[];
}

/** A step reported while the agent works, safe to display as-is. */
export interface AiProgressEvent {
  stage: string;
  tool: string | null;
  message: string;
  records: number | null;
}

/** One line of the streamed response. */
interface AiStreamEvent {
  type: 'progress' | 'done' | 'error';
  stage?: string;
  tool?: string | null;
  message?: string;
  detail?: string;
  records?: number | null;
  response?: AiSendMessageResponse;
}

/**
 * What needs the user's attention, for the opening screen.
 *
 * Counts rather than prose: they are computed in SQL and are exact, so nothing here depends on a
 * model having been right. `scope` describes whose figures these are and belongs beside them.
 */
export interface AiDigest {
  scope: string;
  openActionItems: number;
  overdueActionItems: number;
  visitsAwaitingAction: number;
  awaitingByStatus: { label: string; count: number }[];
  /** True when there is genuinely nothing to report, as distinct from nothing having loaded. */
  isEmpty: boolean;
}

export interface AiCapabilities {
  canUseAssistant: boolean;
  canManageConfiguration: boolean;
  /** Role the server considers primary for this user. */
  role: string;
  /** Role-appropriate opening line for a new chat. */
  greeting: string;
  /** Starter prompts matched to the role. */
  suggestions: string[];
}

/**
 * Chat API client.
 *
 * The user is never identified in a request body — the API derives identity from the bearer
 * token attached by the existing interceptor, so there is no user id for a caller to tamper with.
 */
@Injectable({ providedIn: 'root' })
export class AiChatService {
  private readonly baseUrl = `${environment.APIUrl}/Api/AiChat`;

  constructor(private readonly http: HttpClient) {}

  /** Drives which controls the UI offers. Not an access-control boundary. */
  getCapabilities(): Observable<AiCapabilities> {
    return this.http
      .get<AiCapabilities>(`${this.baseUrl}/Capabilities`)
      .pipe(map(camelizeKeys<AiCapabilities>));
  }

  /**
   * What is waiting for the signed-in user.
   *
   * Scoped by the server in this request, like any other read — the figures are the user's own,
   * and the scope sentence returned alongside them says whose. Shown on the empty state so the
   * assistant reaches people who opened the page without a question in mind.
   */
  getDigest(): Observable<AiDigest> {
    return this.http
      .get<AiDigest>(`${this.baseUrl}/Digest`)
      .pipe(map(camelizeKeys<AiDigest>));
  }

  /**
   * Fetches an evidence file as a blob the page can display.
   *
   * `mediaUrl` is a relative API route, not a storage address — the server holds the file's real
   * location and re-checks access before sending a byte. That is the point: the storage account
   * name is never in the page.
   *
   * It has to be fetched rather than assigned to `<img src>` because an image element cannot send
   * an Authorization header, and this endpoint requires one. The blob URL that comes back belongs
   * to this document alone and reveals nothing about the server.
   */
  getMedia(mediaUrl: string): Observable<Blob> {
    const url = mediaUrl.startsWith('http')
      ? mediaUrl
      : `${environment.APIUrl}${mediaUrl}`;

    return this.http.get(url, { responseType: 'blob' });
  }

  listConversations(): Observable<AiConversationSummary[]> {
    return this.http
      .get<AiConversationSummary[]>(`${this.baseUrl}/Conversations`)
      .pipe(map(camelizeKeys<AiConversationSummary[]>));
  }

  getConversation(conversationId: string): Observable<AiConversationDetail> {
    return this.http
      .get<AiConversationDetail>(`${this.baseUrl}/Conversations/${conversationId}`)
      .pipe(map(camelizeKeys<AiConversationDetail>));
  }

  createConversation(title?: string): Observable<AiConversationSummary> {
    return this.http
      .post<AiConversationSummary>(`${this.baseUrl}/Conversations`, { title })
      .pipe(map(camelizeKeys<AiConversationSummary>));
  }

  renameConversation(conversationId: string, title: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/Conversations/${conversationId}/Rename`, { title });
  }

  deleteConversation(conversationId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/Conversations/${conversationId}`);
  }

  /**
   * Sends a message and reports progress as the agent works.
   *
   * Uses `fetch` with a streamed body rather than `EventSource`, because EventSource cannot set an
   * Authorization header and this API authenticates by bearer token. The server frames each event
   * as one JSON object per line.
   *
   * `onProgress` is called for each step; the promise resolves with the finished turn. A turn that
   * chains several tools runs for minutes, and without this the interface is silent throughout —
   * which a user cannot distinguish from a hang.
   */
  async streamMessage(
    message: string,
    onProgress: (progress: AiProgressEvent) => void,
    conversationId?: string,
    confirmationProvided = false,
    signal?: AbortSignal
  ): Promise<AiSendMessageResponse> {
    const stored = localStorage.getItem('currentUser');
    const session = stored ? JSON.parse(stored) : {};
    const token = session.access_Token ?? session.access_token;

    // This call goes out through `fetch`, so ApiInterceptor never sees it and the headers it
    // normally attaches have to be set by hand. Without the APIM subscription key the gateway
    // rejects the request with 401 before it ever reaches the API.
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `${session.token_type ?? 'Bearer'} ${token}`
    };

    if (session.subscriptionKey) {
      headers['Ocp-Apim-Subscription-Key'] = session.subscriptionKey;
    }

    const response = await fetch(`${this.baseUrl}/StreamMessage`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        conversationId: conversationId ?? null,
        message,
        confirmationProvided
      }),
      signal
    });

    if (!response.ok || !response.body) {
      throw new Error(`Stream failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: AiSendMessageResponse | undefined;
    let failure: string | undefined;

    // Lines can be split across chunks, so anything after the last newline is held back until the
    // rest of it arrives. Parsing a partial line would throw on valid data.
    const consume = (line: string) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return;
      }

      const event = camelizeKeys<AiStreamEvent>(JSON.parse(trimmed));

      if (event.type === 'progress' && event.message) {
        onProgress({
          stage: event.stage ?? 'thinking',
          tool: event.tool ?? null,
          message: event.message,
          records: event.records ?? null
        });
      } else if (event.type === 'done' && event.response) {
        result = event.response;
      } else if (event.type === 'error') {
        failure = event.detail || event.message || 'The assistant is unavailable.';
      }
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      lines.forEach(consume);
    }

    consume(buffer);

    if (failure) {
      throw new Error(failure);
    }

    if (!result) {
      throw new Error('The assistant did not return an answer.');
    }

    return result;
  }

  sendMessage(
    message: string,
    conversationId?: string,
    confirmationProvided = false
  ): Observable<AiSendMessageResponse> {
    return this.http
      .post<AiSendMessageResponse>(`${this.baseUrl}/SendMessage`, {
        conversationId: conversationId ?? null,
        message,
        confirmationProvided
      })
      .pipe(map(camelizeKeys<AiSendMessageResponse>));
  }
}
